import { CheckCircle } from 'lucide-react';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Image4 from "../../Assets/urbania12seater/12seater1.jpg";
import Image5 from "../../Assets/urbania12seater/12seater2.jpg";
import Image6 from "../../Assets/urbania12seater/12seater.webp";
import { 
  Car, 
  Users, 
  Briefcase, 
  Wind, 
  Music, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar, 
  Clock,
  ArrowLeft,
  Star,
  Shield,
  Zap
} from 'lucide-react';

const Urbania12SeaterBangalore = () => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [showBookingForm, setShowBookingForm] = React.useState(false);
  
  const images = [Image4, Image5, Image6];
  
  const vehicleSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "12 Seater Force Urbania Booking Bangalore | Premium Van Rental - Nava Cabs",
    "description": "Book 12-seater Force Urbania premium van in Bangalore. AC, reclining seats, entertainment system. Ideal for family trips & corporate outings.",
    "model": "Force Urbania 12 Seater",
    "vehicleTransmission": "Manual",
    "numberOfSeats": "12"
  };

  const features = [
    'Premium Air Conditioning',
    'Reclining Luxury Seats',
    'Entertainment System',
    'Experienced Driver',
    'Safety Equipment',
    'Real-time GPS Tracking',
    'Spacious Interiors',
    'Mobile Charging Ports',
    'Privacy Curtains',
    'Reading Lights'
  ];

  const specifications = {
    'Vehicle Type': 'Force Urbania Premium Van',
    'Seating Capacity': '12 Passengers',
    'Engine': '2.2L BS6 Diesel',
    'Transmission': 'Manual',
    'Luggage Capacity': '10-12 Large Bags',
    'AC': 'Powerful AC System',
    'Fuel Type': 'Diesel',
    'Fuel Efficiency': '11-13 km/l',
    'Entertainment': 'Music System',
    'Comfort': 'Reclining Luxury Seats'
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => prev === 0 ? images.length - 1 : prev - 1);

  if (showBookingForm) {
    return <BookingForm vehicle={{
      name: '12 Seater Force Urbania',
      seating: 12,
      priceRange: '₹35-39/km'
    }} onBack={() => setShowBookingForm(false)} />;
  }

  return (
    <>
      <Helmet>
        <title>12 Seater Force Urbania Booking Bangalore | Premium Van @ ₹35-39/km - Nava Cabs</title>
        <meta 
          name="description" 
          content="✅ Book 12-seater Force Urbania premium van in Bangalore. AC, reclining seats, entertainment system. Perfect for family trips, corporate outings & airport transfers."
        />
        <meta name="keywords" content="12 seater urbania booking bangalore, force urbania 12 seater, premium van rental bangalore, family travel van bangalore, group transport bangalore" />
        <script type="application/ld+json">{JSON.stringify(vehicleSchema)}</script>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Back Button */}
        <div className="bg-white shadow-sm">
          <div className="container mx-auto px-4 py-2">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors text-sm"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              12 Seater Force Urbania Premium Van Booking in Bangalore
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              Premium 12-seater Force Urbania van rental service for family trips, 
              corporate outings, airport transfers, and group travel from Bangalore.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Users className="w-4 h-4 mr-1.5" /> 12 Seater
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Car className="w-4 h-4 mr-1.5" /> Premium Van
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                ₹35-39/km
              </div>
              <div className="bg-white/20 px-3 py-1.5 rounded-full flex items-center text-sm">
                <Wind className="w-4 h-4 mr-1.5" /> Full AC
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery */}
              <div className="bg-white rounded-lg shadow p-3">
                <div className="relative rounded-md overflow-hidden">
                  <img 
                    src={images[currentImageIndex]} 
                    alt="12 Seater Force Urbania premium van interior and exterior views - Nava Cabs Bangalore"
                    className="w-full h-56 md:h-64 object-cover"
                  />
                  <button 
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full"
                  >
                    ←
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 p-1.5 rounded-full"
                  >
                    →
                  </button>
                </div>
              </div>

              <div className="lg:hidden mt-6">
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-blue-600 mb-1">₹16-22/km</div>
                  <div className="text-gray-600 text-sm">Starting Price</div>
                </div>
                <button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl"
                >
                  Book Urbania 12 Seater Now
                </button>
                
                {/* Quick Contact on Mobile */}
                <div className="mt-4 space-y-3">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-3 rounded-xl hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-bold">+91 99647 58999</div>
                      <div className="text-xs">Call for Instant Booking</div>
                    </div>
                  </a>
                  <a 
                    href="https://wa.me/919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-600 text-white p-3 rounded-xl hover:bg-green-700 transition-colors"
                  >
                    <div className="font-bold">WhatsApp Booking</div>
                  </a>
                </div>
              </div>

              {/* SEO Content */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Force Urbania 12 Seater Premium Van Rental Service in Bangalore
                </h2>
                
                <div className="text-gray-700 space-y-3 text-sm">
                  <p>
                    <strong>Nava Cabs provides premium 12-seater Force Urbania van booking services in Bangalore</strong> 
                    for comfortable group travel. Our Urbania vans are perfect for 
                    <strong> family vacations, corporate team outings, wedding guest transportation, and religious tours</strong>.
                  </p>
                  
                  <p>
                    Featuring <strong>12 luxury reclining seats with ample legroom</strong>, our Urbania vans ensure 
                    a comfortable journey. The vehicles are equipped with 
                    <strong> entertainment system, USB charging ports, and real-time GPS tracking</strong> for a safe trip.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Ideal for These Routes</h3>
                  <p>
                    Our 12-seater Urbania is perfect for <strong>trips from Bangalore to Nandi Hills, Mysore, 
                    Coorg, Ooty, Kabini, Shivanasamudra, and Hogenakkal</strong>. Great for weekend getaways 
                    and corporate offsite meetings.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Service Areas in Bangalore</h3>
                  <p>
                    We offer <strong>Urbania van services throughout Bangalore</strong> covering Whitefield, 
                    Electronic City, Koramangala, Indiranagar, HSR Layout, JP Nagar, Banashankari, 
                    Rajajinagar, Malleshwaram, Yeshwanthpur, and Airport transfers from Kempegowda International Airport.
                  </p>
                  
                  <h3 className="text-xl font-bold mt-6 mb-3">Benefits of Our 12 Seater Urbania</h3>
                  <ul className="grid md:grid-cols-2 gap-2">
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> 24/7 Service</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Experienced Drivers</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> GPS Enabled</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Transparent Pricing</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Regular Maintenance</li>
                    <li className="flex items-center text-sm"><CheckCircle className="w-4 h-4 text-green-500 mr-2" /> Insured Vehicles</li>
                  </ul>
                </div>
              </div>

              {/* Features */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Urbania 12 Seater Features</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center p-2.5 bg-blue-50 rounded-md text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-600 mr-2.5" />
                      <span className="text-gray-800">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Specifications */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(specifications).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 p-3 rounded-md">
                      <div className="text-xs text-gray-600 font-medium">{key}</div>
                      <div className="text-base font-semibold text-gray-900">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
             <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-5 sticky top-20">
                <div className="text-center mb-5">
                  <div className="text-3xl font-bold text-blue-600 mb-1">₹35-39/km</div>
                  <div className="text-gray-600 text-sm">Starting Price</div>
                  <div className="text-xs text-gray-500 mt-1">*Price varies based on trip type & duration</div>
                </div>

                <button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-lg font-bold text-base mb-5 hover:from-blue-700 hover:to-blue-800 transition-all shadow hover:shadow-md"
                >
                  Book Urbania Now
                </button>

                <div className="space-y-3">
                  <a 
                    href="tel:+919964758999" 
                    className="flex items-center justify-center gap-3 bg-green-50 text-green-800 p-3 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <div className="text-left">
                      <div className="font-bold">+91 99647 58999</div>
                      <div className="text-xs">Call for Instant Booking</div>
                    </div>
                  </a>

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-900 mb-3">Quick Enquiry</h3>
                    <div className="space-y-2">
                      <a href="https://wa.me/919964758999" className="block bg-green-600 text-white p-2.5 rounded-md text-center hover:bg-green-700 text-sm">
                        WhatsApp: +91 99647 58999
                      </a>
                      <a href="mailto:info@navacabs.com" className="block bg-blue-600 text-white p-2.5 rounded-md text-center hover:bg-blue-700 text-sm">
                        Email: info@navacabs.com
                      </a>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="font-bold text-gray-900 mb-3">Service Highlights</h3>
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <Shield className="w-4 h-4 text-green-600" />
                        <div>
                          <div className="font-semibold text-sm">Insured Vehicles</div>
                          <div className="text-xs text-gray-600">Full insurance coverage</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <div>
                          <div className="font-semibold text-sm">4.8/5 Rating</div>
                          <div className="text-xs text-gray-600">500+ happy customers</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <div>
                          <div className="font-semibold text-sm">All Bangalore Areas</div>
                          <div className="text-xs text-gray-600">Wide service coverage</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


const BookingForm = ({ vehicle, onBack }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    dropDate: '',
    dropTime: '',
    tripType: 'oneway',
    passengers: '',
    specialRequests: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }
    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Pickup location is required';
    if (!formData.dropLocation.trim()) newErrors.dropLocation = 'Drop location is required';
    if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
    if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
    
    if (formData.tripType === 'roundtrip') {
      if (!formData.dropDate) newErrors.dropDate = 'Return date is required';
      if (!formData.dropTime) newErrors.dropTime = 'Return time is required';
    }
    
    if (!formData.passengers) {
      newErrors.passengers = 'Number of passengers is required';
    } else if (parseInt(formData.passengers) > vehicle.seating) {
      newErrors.passengers = `Maximum ${vehicle.seating} passengers allowed`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

   const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };


  const sendWhatsAppMessage = () => {
    // Format the message
    const message = `*New Booking Request - Nava Cabs*

*Customer Details:*
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Passengers: ${formData.passengers}

*Trip Details:*
Vehicle: ${vehicle.name} (${vehicle.seating} seater)
Pickup: ${formData.pickupLocation}
Drop: ${formData.dropLocation}
Trip Type: ${formData.tripType === 'oneway' ? 'One Way' : 'Round Trip'}

*Schedule:*
Pickup Date: ${formatDate(formData.pickupDate)}
Pickup Time: ${formData.pickupTime}
${formData.tripType === 'roundtrip' ? `Return Date: ${formatDate(formData.dropDate)}\nReturn Time: ${formData.dropTime}` : ''}

*Special Requests:* ${formData.specialRequests || 'None'}

*Estimated Price:* ${vehicle.priceRange} (Contact for exact quote)

---
*Submitted via Nava Cabs Website*`;

    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // WhatsApp number (use your business number)
    const whatsappNumber = '919916788688';
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappURL, '_blank');
  };


   const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Send to WhatsApp
      sendWhatsAppMessage();
      
      // Also send to your backend if needed
      const bookingData = {
        ...formData,
        vehicle: vehicle.name,
        vehicleType: vehicle.type,
        priceRange: vehicle.priceRange,
        submittedAt: new Date().toISOString()
      };
      
      // You can also send to your API endpoint
      console.log('Booking Data for API:', bookingData);
      
      // Show success message
      alert('Booking request submitted successfully! Opening WhatsApp to confirm details...');
      
      // Close form after 2 seconds
      setTimeout(() => {
        setIsSubmitting(false);
        onBack();
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      alert('Error submitting booking. Please try again.');
      setIsSubmitting(false);
    }
  };
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="container mx-auto px-4">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span className="font-medium">Back to Vehicle Details</span>
        </button>

        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow p-5">
            <div className="mb-5">
              <h1 className="text-lg font-bold text-gray-800 mb-1">Book Your Ride</h1>
              <p className="text-gray-600 text-xs">Complete the form below to book {vehicle.name}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Information */}
              <div>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3 text-blue-600" />
                  Personal Information
                </h2>
                <div className="grid md:grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder="Enter your full name"
                    />
                    {errors.name && <p className="text-red-500 text-xs mt-0.5">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder="your@email.com"
                    />
                    {errors.email && <p className="text-red-500 text-xs mt-0.5">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder="10-digit mobile number"
                    />
                    {errors.phone && <p className="text-red-500 text-xs mt-0.5">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Passengers *
                    </label>
                    <input
                      type="number"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleChange}
                      min="1"
                      max={vehicle.seating}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.passengers ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder={`Max ${vehicle.seating}`}
                    />
                    {errors.passengers && <p className="text-red-500 text-xs mt-0.5">{errors.passengers}</p>}
                  </div>
                </div>
              </div>

              {/* Trip Details */}
              <div>
                <h2 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-blue-600" />
                  Trip Details
                </h2>
                
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Trip Type *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'oneway' }))}
                      className={`py-1.5 px-3 rounded border text-xs font-medium ${
                        formData.tripType === 'oneway'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      One Way
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, tripType: 'roundtrip' }))}
                      className={`py-1.5 px-3 rounded border text-xs font-medium ${
                        formData.tripType === 'roundtrip'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      Round Trip
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-2.5">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Location *
                    </label>
                    <input
                      type="text"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.pickupLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder="Enter pickup location"
                    />
                    {errors.pickupLocation && <p className="text-red-500 text-xs mt-0.5">{errors.pickupLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Drop Location *
                    </label>
                    <input
                      type="text"
                      name="dropLocation"
                      value={formData.dropLocation}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.dropLocation ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                      placeholder="Enter drop location"
                    />
                    {errors.dropLocation && <p className="text-red-500 text-xs mt-0.5">{errors.dropLocation}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Date *
                    </label>
                    <input
                      type="date"
                      name="pickupDate"
                      value={formData.pickupDate}
                      onChange={handleChange}
                      min={today}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.pickupDate ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                    />
                    {errors.pickupDate && <p className="text-red-500 text-xs mt-0.5">{errors.pickupDate}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Time *
                    </label>
                    <input
                      type="time"
                      name="pickupTime"
                      value={formData.pickupTime}
                      onChange={handleChange}
                      className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                        errors.pickupTime ? 'border-red-500' : 'border-gray-300'
                      } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                    />
                    {errors.pickupTime && <p className="text-red-500 text-xs mt-0.5">{errors.pickupTime}</p>}
                  </div>

                  {formData.tripType === 'roundtrip' && (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Date *
                        </label>
                        <input
                          type="date"
                          name="dropDate"
                          value={formData.dropDate}
                          onChange={handleChange}
                          min={formData.pickupDate || today}
                          className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                            errors.dropDate ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                        />
                        {errors.dropDate && <p className="text-red-500 text-xs mt-0.5">{errors.dropDate}</p>}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Return Time *
                        </label>
                        <input
                          type="time"
                          name="dropTime"
                          value={formData.dropTime}
                          onChange={handleChange}
                          className={`w-full px-2.5 py-1.5 rounded border text-xs ${
                            errors.dropTime ? 'border-red-500' : 'border-gray-300'
                          } focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none`}
                        />
                        {errors.dropTime && <p className="text-red-500 text-xs mt-0.5">{errors.dropTime}</p>}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Special Requests (Optional)
                </label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-2.5 py-1.5 rounded border border-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-transparent outline-none resize-none text-xs"
                  placeholder="Any special requirements or requests..."
                ></textarea>
              </div>

              {/* Vehicle Summary */}
              <div className="bg-blue-50 rounded p-3 border border-blue-200">
                <h3 className="font-semibold text-gray-800 mb-1 text-xs">Selected Vehicle</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800 text-xs">{vehicle.name}</p>
                    <p className="text-xs text-gray-600">{vehicle.seating} Seater</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-600">Starting from</p>
                    <p className="text-sm font-bold text-blue-600">{vehicle.priceRange}</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={onBack}
                  className="flex-1 py-2 px-4 rounded border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-all text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2 px-4 rounded bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                </button>
              </div>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our terms and conditions.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Urbania12SeaterBangalore;